import type { Locale } from "./types";

const INTL_LOCALE: Record<Locale, string> = { fr: "fr-FR", en: "en-GB" };

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat | Intl.DisplayNames>();

function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat | Intl.DisplayNames>(
  key: string,
  build: () => T,
): T {
  const hit = cache.get(key);
  if (hit) return hit as T;
  const value = build();
  cache.set(key, value);
  return value;
}

/** Amounts are stored as integer minor units (cents). */
export function formatMoney(cents: number, currency: string, locale: Locale): string {
  const nf = memo(
    `money:${locale}:${currency}`,
    () => new Intl.NumberFormat(INTL_LOCALE[locale], { style: "currency", currency }),
  );
  return nf.format(cents / 100);
}

export function formatNumber(value: number, locale: Locale, options?: Intl.NumberFormatOptions): string {
  const nf = memo(`number:${locale}:${JSON.stringify(options ?? {})}`, () => new Intl.NumberFormat(INTL_LOCALE[locale], options));
  return nf.format(value);
}

/** `ratio` is a fraction: 0.253 → 25,3 %. */
export function formatPercent(ratio: number, locale: Locale, fractionDigits = 1): string {
  return formatNumber(ratio, locale, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

export type DateStyle = "short" | "medium" | "long" | "datetime" | "time";

const DATE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { dateStyle: "short" },
  medium: { dateStyle: "medium" },
  long: { dateStyle: "long" },
  datetime: { dateStyle: "medium", timeStyle: "short" },
  time: { timeStyle: "short" },
};

/**
 * A calendar day ("2026-10-15", or the UTC midnight Date the API client turns it into) has no time
 * zone: it is printed as is, never shifted to the previous day on a device west of UTC.
 */
function isCalendarDay(value: Date | string): boolean {
  if (typeof value === "string") return /^\d{4}-\d{2}-\d{2}$/.test(value);
  return (
    value.getUTCHours() === 0 && value.getUTCMinutes() === 0 && value.getUTCSeconds() === 0 && value.getUTCMilliseconds() === 0
  );
}

export function formatDate(value: Date | string, locale: Locale, style: DateStyle = "medium"): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const day = (style === "short" || style === "medium" || style === "long") && isCalendarDay(value);
  const df = memo(
    `date:${locale}:${style}:${day ? "utc" : "local"}`,
    () => new Intl.DateTimeFormat(INTL_LOCALE[locale], day ? { ...DATE_OPTIONS[style], timeZone: "UTC" } : DATE_OPTIONS[style]),
  );
  return df.format(date);
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

export function formatRelative(value: Date | string, locale: Locale, now: Date = new Date()): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const rtf = memo(`relative:${locale}`, () => new Intl.RelativeTimeFormat(INTL_LOCALE[locale], { numeric: "auto" }));
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size || unit === "second") {
      return rtf.format(Math.round(seconds / size), unit);
    }
  }
  return rtf.format(0, "second");
}

export function countryName(code: string, locale: Locale): string {
  const dn = memo(`region:${locale}`, () => new Intl.DisplayNames([INTL_LOCALE[locale]], { type: "region" }));
  return dn.of(code.toUpperCase()) ?? code;
}

export function currencyName(code: string, locale: Locale): string {
  const dn = memo(`currency:${locale}`, () => new Intl.DisplayNames([INTL_LOCALE[locale]], { type: "currency" }));
  return dn.of(code.toUpperCase()) ?? code;
}

export interface PostalAddress {
  line1: string;
  line2?: string | null;
  postalCode: string;
  city: string;
  country: string;
}

/** Postal ordering differs by country; FR/BE/DE/… put the postal code before the city. */
const CITY_FIRST = new Set(["US", "GB", "CN", "CA", "AU"]);

export function formatAddressLines(address: PostalAddress, locale: Locale, homeCountry = "FR"): string[] {
  const country = address.country.toUpperCase();
  const cityLine = CITY_FIRST.has(country)
    ? `${address.city} ${address.postalCode}`.trim()
    : `${address.postalCode} ${address.city}`.trim();
  const lines = [address.line1, address.line2 ?? "", cityLine];
  if (country !== homeCountry) lines.push(countryName(country, locale));
  return lines.filter((line) => line.length > 0);
}

export function intlLocale(locale: Locale): string {
  return INTL_LOCALE[locale];
}

/** Month label from a "YYYY-MM" key: "sept." (short) or "sept. 2026" (long). */
export function formatMonth(yearMonth: string, locale: Locale, style: "short" | "long" = "short"): string {
  const df = memo(
    `month:${locale}:${style}`,
    () =>
      new Intl.DateTimeFormat(
        INTL_LOCALE[locale],
        style === "short" ? { month: "short", timeZone: "UTC" } : { month: "short", year: "numeric", timeZone: "UTC" },
      ),
  );
  return df.format(new Date(`${yearMonth}-01T12:00:00Z`));
}
